import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supplierAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

export default function SupplierMessages() {
    const { user } = useAuth();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);
    const pollRef = useRef(null);

    const loadMessages = useCallback(async () => {
        try {
            const res = await supplierAPI.getMessages();
            setMessages(res.data.messages || []);
        } catch (err) {
            if (!loading) toast.error('Erreur chargement messages');
        } finally {
            setLoading(false);
        }
    }, [loading]);

    useEffect(() => {
        loadMessages();
        pollRef.current = setInterval(loadMessages, 5000);
        return () => clearInterval(pollRef.current);
    }, [loadMessages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        setSending(true);
        try {
            await supplierAPI.sendMessage({ text: newMessage.trim() });
            setNewMessage('');
            loadMessages();
        } catch (err) {
            toast.error('Erreur envoi');
        } finally {
            setSending(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-gray-400">Chargement...</div></div>;

    return (
        <div className="h-[calc(100vh-140px)] flex flex-col" data-testid="supplier-messages">
            <div className="mb-4">
                <h1 className="text-2xl font-bold text-gray-900">Messages</h1>
                <p className="text-sm text-gray-500">Echangez avec l'equipe PRIXDEAL</p>
            </div>

            <Card className="rounded-xl flex-1 flex flex-col overflow-hidden">
                <CardHeader className="py-3 px-4 border-b bg-emerald-50">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <MessageCircle className="w-4 h-4 text-emerald-600" />
                        Administration PRIXDEAL
                    </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                    {messages.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p>Aucun message. Commencez la conversation !</p>
                        </div>
                    ) : (
                        messages.map(msg => {
                            const isMe = msg.sender_role === 'supplier';
                            return (
                                <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${isMe ? 'bg-emerald-600 text-white rounded-br-md' : 'bg-gray-100 text-gray-800 rounded-bl-md'}`}
                                        data-testid={`message-${msg.id}`}>
                                        {!isMe && <p className="text-[10px] font-semibold mb-0.5 opacity-70">{msg.sender_name}</p>}
                                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                                        <p className={`text-[10px] mt-1 ${isMe ? 'text-emerald-200' : 'text-gray-400'}`}>
                                            {new Date(msg.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                                        </p>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    <div ref={messagesEndRef} />
                </CardContent>

                <form onSubmit={handleSend} className="p-3 border-t flex gap-2 bg-white">
                    <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Ecrivez votre message..."
                        className="flex-1 rounded-xl"
                        data-testid="message-input"
                    />
                    <Button
                        type="submit"
                        disabled={sending || !newMessage.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl px-4"
                        data-testid="send-message-btn"
                    >
                        <Send className="w-4 h-4" />
                    </Button>
                </form>
            </Card>
        </div>
    );
}
